/**
 * Audit Logger Utility
 * 
 * Provides append-only audit logging for sensitive operations.
 * This utility never throws errors - logging failures are silent to prevent
 * breaking business logic.
 */

import { supabase } from '../lib/supabaseClient';
import { logger } from './logger';
import { UserRole } from '../types/user';

export interface AuditEventInput {
  orgId: string;
  actorUserId: string;
  actorRole: UserRole;
  actionType: string;
  entityType: string;
  entityId?: string | null;
  description?: string;
  metadata?: Record<string, any>;
}

/**
 * Log an audit event
 * 
 * This function is fail-safe: if logging fails, it logs the error but does not throw.
 * Business operations should never be blocked by audit logging failures.
 * 
 * @param event - Audit event data
 */
export async function logAuditEvent(event: AuditEventInput): Promise<void> {
  try {
    // Generate description if not provided
    const description = event.description || generateDescription(event);

    const auditData = {
      org_id: event.orgId,
      actor_user_id: event.actorUserId,
      actor_role: event.actorRole,
      action_type: event.actionType,
      entity_type: event.entityType,
      entity_id: event.entityId || null,
      description,
      metadata: event.metadata || null,
      created_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('audit_logs')
      .insert(auditData);

    if (error) {
      // Log error but don't throw - audit logging should never break operations
      const err = error as { message?: string; code?: string; details?: unknown };
      if (err.code === '42501') {
        logger.error('[RLS_VIOLATION] Audit log INSERT blocked by RLS policy', {
          table: 'audit_logs',
          operation: 'INSERT',
          errorCode: err.code,
          errorMessage: err.message,
          errorDetails: err.details,
        });
      }
      logger.error('[AuditLogger] Failed to log audit event', {
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
      });
    }
  } catch (error) {
    // Catch any unexpected errors and log them, but don't throw
    logger.error('[AuditLogger] Unexpected error logging audit event:', error);
  }
}

/**
 * Generate a human-readable description from event data
 */
function generateDescription(event: AuditEventInput): string {
  const actorName = event.actorRole === 'admin' ? 'Admin' : 
                   event.actorRole === 'dispatch' ? 'Dispatch' :
                   event.actorRole === 'cfi' ? 'CFI' :
                   event.actorRole === 'student' ? 'Student' :
                   event.actorRole === 'member' ? 'Member' :
                   event.actorRole === 'maintenance' ? 'Maintenance' :
                   'User';

  const action = formatActionType(event.actionType);
  const entity = formatEntityType(event.entityType);

  if (event.entityId) {
    return `${entity} ${action} by ${actorName} (${event.actorUserId})`;
  }

  return `${entity} ${action} by ${actorName} (${event.actorUserId})`;
}

/**
 * Format action type for human-readable descriptions
 */
function formatActionType(actionType: string): string {
  const actionMap: Record<string, string> = {
    'CREATE': 'created',
    'UPDATE': 'updated',
    'DELETE': 'deleted',
    'CANCEL': 'cancelled',
    'COMPLETE': 'completed',
    'ARCHIVE': 'archived',
    'GROUND': 'marked as grounded',
    'UNGROUND': 'marked as ungrounded',
    'CLOSE': 'closed',
    'RESOLVE': 'resolved',
    'END': 'ended',
    'PERMISSION_DENIED': 'permission denied',
  };

  return actionMap[actionType] || actionType.toLowerCase();
}

/**
 * Format entity type for human-readable descriptions
 */
function formatEntityType(entityType: string): string {
  const entityMap: Record<string, string> = {
    'BOOKING': 'Booking',
    'AIRCRAFT': 'Aircraft',
    'MAINTENANCE_REPORT': 'Maintenance report',
    'ASSIGNMENT': 'Assignment',
    'MEMBER': 'Member',
    'MAINTENANCE_INTERVAL': 'Maintenance interval',
    'PERMISSION': 'Permission',
  };

  return entityMap[entityType] || entityType;
}

/**
 * Helper to log booking events with consistent formatting
 */
export async function logBookingEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'CANCEL' | 'COMPLETE',
  bookingId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'BOOKING',
    entityId: bookingId,
    description,
    metadata,
  });
}

/**
 * Helper to log aircraft events with consistent formatting
 */
export async function logAircraftEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'ARCHIVE' | 'GROUND' | 'UNGROUND' | 'UPDATE_HOURS',
  aircraftId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'AIRCRAFT',
    entityId: aircraftId,
    description,
    metadata,
  });
}

/**
 * Helper to log maintenance report events
 */
export async function logMaintenanceEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'CLOSE' | 'RESOLVE',
  reportId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'MAINTENANCE_REPORT',
    entityId: reportId,
    description,
    metadata,
  });
}

/**
 * Helper to log assignment events
 */
export async function logAssignmentEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'END',
  assignmentId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'ASSIGNMENT',
    entityId: assignmentId,
    description,
    metadata,
  });
}

/**
 * Helper to log member-management events (role changes, removals).
 * actorUserId/actorRole identify who performed the action; memberId is the
 * affected member. Metadata should carry the before/after detail
 * (e.g. previousRole/newRole, or removedUserEmail/removedUserRole).
 */
export async function logMemberEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE',
  memberId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'MEMBER',
    entityId: memberId,
    description,
    metadata,
  });
}

/**
 * Helper to log maintenance interval item changes (create/update/delete).
 * entityId is the interval item id; metadata should carry aircraftId and the
 * item name so the audit trail is meaningful.
 */
export async function logMaintenanceIntervalEvent(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  actionType: 'CREATE' | 'UPDATE' | 'DELETE',
  intervalItemId: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType,
    entityType: 'MAINTENANCE_INTERVAL',
    entityId: intervalItemId,
    description,
    metadata,
  });
}

/**
 * Helper to log permission denial events
 */
export async function logPermissionDenied(
  orgId: string,
  actorUserId: string,
  actorRole: UserRole,
  permission: string,
  actionDescription: string,
  metadata?: Record<string, any>
): Promise<void> {
  await logAuditEvent({
    orgId,
    actorUserId,
    actorRole,
    actionType: 'PERMISSION_DENIED',
    entityType: 'PERMISSION',
    entityId: null,
    description: `Permission denied: ${permission} attempted by ${actorRole} for "${actionDescription}"`,
    metadata: {
      permission,
      actionDescription,
      ...metadata,
    },
  });
}


